import { addPinchZoomListener } from 'lib/events-framework/pinchZoomListener'
import { useClickListener } from 'lib/events-framework/useClickListener'
import { useDoubleClickListener } from 'lib/events-framework/useDoubleClickListener'
import { usePinchZoomListener } from 'lib/events-framework/usePinchZoomListener'
import { useRelativeDrag } from 'lib/events-framework/useRelativeDrag'
// import { useRelativeDrag } from 'lib/use-relative-drag'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useMultiStateValidator } from 'react-use'
import styled from 'styled-components'

export type Props = {
  resetZoom?: {}
  isVisible: boolean
  onTouchClick: () => void
  onIsZoomedChange: (value: boolean) => void
  naturalHeight?: null | number
  naturalWidth?: null | number
  width?: null | number
}

export const ZoomableContainer: FC<Props> = ({
  children,
  isVisible,
  onTouchClick,
  onIsZoomedChange,
  naturalHeight = null,
  naturalWidth = null,
  width = null
}) => {
  const [isZoomed, setIsZoomed] = useState(false)
  const [translation, setTranslation] = useState<[number, number]>([0, 0])
  const [scaleOnZoom, setScaleOnZoom] = useState<null | number>(null)
  const [initialPosition, setInitialPosition] = useState<{ top: number; bottom: number; left: number; right: number }>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  })
  const [transformOrigin, setTransformOrigin] = useState<[number, number]>([0, 0])
  const [isFirstMove, setIsFirstMove] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (width !== null && naturalWidth !== null) {
      const scaleForFullImage = naturalWidth / width
      setScaleOnZoom(scaleForFullImage === 1 ? null : scaleForFullImage)
    }
  }, [width, naturalWidth])

  const sliderContentTransform = `translate(${translation[0]}px, ${translation[1]}px) ${
    isZoomed ? `scale(${scaleOnZoom !== null ? scaleOnZoom : 1})` : `scale(1)`
  }`

  const clickHandler = useCallback(
    (e, pointerType) => {
      if (pointerType === 'mouse' && scaleOnZoom !== null) {
        setIsZoomed((value) => !value)
      }
    },
    [scaleOnZoom]
  )

  const doubleClickHandler = useCallback(
    (e, clickType) => {
      if (clickType === 'double' && scaleOnZoom !== null) {
        setIsZoomed((value) => !value)
      } else {
        onTouchClick()
      }
    },
    [onTouchClick, scaleOnZoom]
  )

  useClickListener(containerRef, clickHandler)
  useDoubleClickListener(containerRef, doubleClickHandler, 'touch')
  //TODO: добавить pinch zoom
  // usePinchZoomListener(containerRef, pinchZoomHandler)

  useEffect(() => {
    if (!isVisible) {
      setIsZoomed(false)
    }
  }, [isVisible])

  useEffect(() => {
    if (!isZoomed) {
      setTranslation([0, 0])
    }
  }, [isZoomed])

  const limitTranslation = ([x, y]: [number, number], isDecelerationOn: boolean) => {
    let topMaxMove = -initialPosition.top
    let bottomMaxMove = -initialPosition.bottom
    let leftMaxMove = -initialPosition.left
    let rightMaxMove = -initialPosition.right

    if (initialPosition.top > 0 || initialPosition.bottom > 0) {
      if (initialPosition.top > initialPosition.bottom) {
        topMaxMove = 0
        bottomMaxMove = initialPosition.top - initialPosition.bottom
      } else {
        topMaxMove = initialPosition.bottom - initialPosition.top
        bottomMaxMove = 0
      }
    }

    if (initialPosition.left > 0 || initialPosition.right > 0) {
      if (initialPosition.left > initialPosition.right) {
        leftMaxMove = 0
        rightMaxMove = initialPosition.left - initialPosition.right
      } else {
        leftMaxMove = initialPosition.right - initialPosition.left
        rightMaxMove = 0
      }
    }

    let newX = x
    let newY = y

    if (leftMaxMove - x < 0) {
      newX = leftMaxMove + (isDecelerationOn ? (x - leftMaxMove) ** 0.85 : 0)
    } else if (rightMaxMove + x < 0) {
      newX = -rightMaxMove + (isDecelerationOn ? Math.sign(x) * Math.abs(x + rightMaxMove) ** 0.85 : 0)
    }
    if (topMaxMove - y < 0) {
      newY = topMaxMove + (isDecelerationOn ? (y - topMaxMove) ** 0.85 : 0)
    } else if (bottomMaxMove + y < 0) {
      newY = -bottomMaxMove + (isDecelerationOn ? Math.sign(y) * Math.abs(y + bottomMaxMove) ** 0.85 : 0)
    }

    return [newX, newY] as [number, number]
  }

  const moveContainer = useCallback(
    ([x, y]) => {
      setTranslation(limitTranslation([x, y], true))
    },
    [initialPosition]
  )

  const onDrop = () => {
    setTranslation(([x, y]) => {
      return limitTranslation([x, y], false)
    })
  }

  const { pick, picked } = useRelativeDrag({
    position: translation,
    onPositionChange: moveContainer,
    onDrop
  })

  useEffect(() => {
    onIsZoomedChange(isZoomed)
  }, [isZoomed])

  return (
    <Container
      ref={containerRef}
      onPointerDown={(e) => {
        if (isZoomed) {
          pick(e)
          if (!containerRef.current || !isFirstMove) return
          setIsFirstMove(false)
          const { width, height, top, left } = containerRef.current.getBoundingClientRect()
          const relativeBottom = document.documentElement.clientHeight - height - top
          const relativeRight = document.documentElement.clientWidth - width - left
          setInitialPosition({ left, top, bottom: relativeBottom, right: relativeRight })
        } else {
          setIsFirstMove(true)
          if (!containerRef.current) return
          const y = containerRef.current.getBoundingClientRect().top
          const x = containerRef.current.getBoundingClientRect().left
          setTransformOrigin([e.clientX - x, e.clientY - y])
        }
      }}
      style={{
        transform: sliderContentTransform,
        transformOrigin: `${transformOrigin[0]}px ${transformOrigin[1]}px`
      }}
      isZoomed={isZoomed}
      picked={picked}
      scaleOnZoom={scaleOnZoom}
    >
      {children}
    </Container>
  )
}

const Container = styled.div<{ isZoomed: boolean; picked: boolean; scaleOnZoom: null | number }>`
  cursor: ${({ isZoomed, picked, scaleOnZoom }) => {
    if (isZoomed) {
      if (picked) return 'grabbing'
      return 'grab'
    } else if (scaleOnZoom !== null) {
      return 'zoom-in'
    } else {
      return 'default'
    }
  }};
  transition: ${({ picked }) => (picked ? 'none' : 'transform 333ms cubic-bezier(.4,0,.22,1)')};
`
